// Navigation du back-office : une seule définition, rangée par thèmes.
// Règle de la maison : une nouvelle fonctionnalité prend place dans un groupe
// (voire dans un onglet d'une page existante), pas dans une page de plus.
import type { Component } from 'vue'
import {
  LayoutDashboard, Users, History, Network, BarChart3, FileText, MapPin, FileBarChart,
  MessageSquare, ScrollText, Workflow, KeyRound, BellRing,
} from 'lucide-vue-next'
import { NAV_SECTION, canAccessSection } from '@/utils/permissions'

export interface NavItem {
  id: string
  label: string
  icon: Component
  route: string
}

export interface NavGroup {
  id: string
  label: string
  items: NavItem[]
}

interface NavOptions {
  roleCode?: string
  canSection: (section: string) => boolean
}

export function buildNavGroups({ roleCode, canSection }: NavOptions): NavGroup[] {
  const ambassadeur = roleCode === 'AMBASSADEUR'
  const admin = roleCode === 'ADMIN'

  const groups: { id: string; label: string; items: (NavItem | false)[] }[] = [
    {
      id: 'pilotage',
      label: 'Pilotage',
      items: [
        { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, route: '/dashboard' },
        { id: 'statistiques', label: 'Statistiques', icon: BarChart3, route: '/statistiques' },
        { id: 'suivi-courriers', label: 'Suivi des courriers', icon: MapPin, route: '/suivi-courriers' },
        { id: 'rapport-journalier', label: 'Rapport journalier', icon: FileBarChart, route: '/rapport-journalier' },
      ],
    },
    {
      id: 'acces',
      label: 'Utilisateurs et accès',
      items: [
        { id: 'users', label: 'Utilisateurs', icon: Users, route: '/users' },
        !ambassadeur && { id: 'roles', label: 'Rôles et droits', icon: KeyRound, route: '/roles' },
      ],
    },
    {
      id: 'organisation',
      label: 'Organisation et circuits',
      items: [
        !ambassadeur && { id: 'organisation', label: 'Organisation', icon: Network, route: '/organisation' },
        { id: 'types-document', label: 'Types de document', icon: FileText, route: '/types-document' },
        { id: 'workflow-templates', label: 'Workflows prédéfinis', icon: Workflow, route: '/workflow-templates' },
      ],
    },
    {
      id: 'communication',
      label: 'Notifications et retours',
      items: [
        !ambassadeur && { id: 'notifications', label: 'Notifications', icon: BellRing, route: '/notifications' },
        !ambassadeur && { id: 'feedback', label: 'Feedback', icon: MessageSquare, route: '/feedback' },
      ],
    },
    {
      id: 'supervision',
      label: 'Supervision',
      items: [
        { id: 'audit', label: "Journal d'audit", icon: History, route: '/audit' },
        // Export des logs serveur : seul le rôle Administrateur (cf. restriction backend).
        admin && { id: 'logs', label: 'Logs', icon: ScrollText, route: '/logs' },
      ],
    },
  ]

  // Un profil restreint ne voit que les sections qui lui sont attribuées ;
  // un groupe sans entrée visible disparaît.
  return groups
    .map((g) => ({
      id: g.id,
      label: g.label,
      items: g.items.filter((i): i is NavItem => !!i && canAccessSection(canSection, NAV_SECTION[i.id])),
    }))
    .filter((g) => g.items.length > 0)
}

// Entrée active : l'adresse courante est celle de l'entrée ou l'une de ses sous-pages.
export function isNavItemActive(path: string, item: NavItem): boolean {
  return path === item.route || path.startsWith(item.route + '/')
}
